import { create } from "@/lib/create-safe-store";
import { getStorage } from "@/lib/storage";
import type { Conversation } from "./types";

function buildUnreadCounts(conversations: Conversation[]) {
  const unreadCounts: Record<string, number> = {};
  for (const conversation of conversations) {
    unreadCounts[conversation.id] = conversation.unread_count || 0;
  }
  return unreadCounts;
}

function mergeConversation(existing: Conversation | undefined, incoming: Conversation) {
  if (!existing) return incoming;
  return {
    ...existing,
    ...incoming,
    // Preserve a discovered bot participant flag so cached/older list payloads
    // do not temporarily hide bot UI for participants after a fresher chat
    // detail response already proved a bot is present.
    has_bot_participant: existing.has_bot_participant || incoming.has_bot_participant,
  };
}

interface ConversationState {
  conversations: Conversation[];
  unreadCounts: Record<string, number>;
  /** Unread chat count per account (for accounts not currently active). */
  crossAccountUnread: Record<string, number>;
  setConversations: (conversations: Conversation[], accountId?: string | null) => void;
  addConversation: (conversation: Conversation) => void;
  updateConversation: (conversation: Conversation) => void;
  removeConversation: (id: string) => void;
  decrementUnread: (conversationId: string) => void;
  incrementUnread: (conversationId: string) => void;
  clearUnread: (conversationId: string) => void;
  incrementCrossAccountUnread: (accountId: string) => void;
  clearCrossAccountUnread: (accountId: string) => void;
  updateLastMessage: (
    conversationId: string,
    timestamp: string,
    text?: string | null,
    senderId?: string | null,
    senderName?: string | null,
  ) => void;
}

export const useConversationStore = create<ConversationState>((set) => ({
  conversations: [],
  unreadCounts: {},
  crossAccountUnread: {},

  setConversations: (conversations, accountId) => {
    const existingById = new Map(
      useConversationStore.getState().conversations.map((conversation) => [conversation.id, conversation]),
    );
    const nextConversations = conversations.map((conversation) =>
      mergeConversation(existingById.get(conversation.id), conversation),
    );
    set((s) => ({
      conversations: nextConversations,
      unreadCounts: buildUnreadCounts(nextConversations),
      // Authoritative server-side unread_count is now loaded for this account;
      // drop the realtime-only fallback so the badge doesn't double-count.
      crossAccountUnread: accountId
        ? { ...s.crossAccountUnread, [accountId]: 0 }
        : s.crossAccountUnread,
    }));
    if (accountId) {
      getStorage().saveConversations(accountId, nextConversations).catch(() => {});
    }
  },

  addConversation: (conversation) =>
    set((s) => {
      if (s.conversations.some((c) => c.id === conversation.id)) return s;
      return {
        conversations: [conversation, ...s.conversations],
        unreadCounts: {
          ...s.unreadCounts,
          [conversation.id]: conversation.unread_count || 0,
        },
      };
    }),

  updateConversation: (conversation) =>
    set((s) => ({
      conversations: s.conversations.map((item) => (item.id === conversation.id
        ? mergeConversation(item, conversation)
        : item)),
      unreadCounts: {
        ...s.unreadCounts,
        [conversation.id]: conversation.unread_count ?? s.unreadCounts[conversation.id] ?? 0,
      },
    })),

  removeConversation: (id) =>
    set((s) => ({
      conversations: s.conversations.filter((c) => c.id !== id),
      unreadCounts: Object.entries(s.unreadCounts).reduce<Record<string, number>>((acc, [conversationId, unreadCount]) => {
        if (conversationId !== id) {
          acc[conversationId] = unreadCount;
        }
        return acc;
      }, {}),
    })),

  decrementUnread: (conversationId) =>
    set((s) => ({
      unreadCounts: {
        ...s.unreadCounts,
        [conversationId]: Math.max(0, (s.unreadCounts[conversationId] || 0) - 1),
      },
    })),

  incrementUnread: (conversationId) =>
    set((s) => ({
      unreadCounts: {
        ...s.unreadCounts,
        [conversationId]: (s.unreadCounts[conversationId] || 0) + 1,
      },
    })),

  clearUnread: (conversationId) =>
    set((s) => ({
      unreadCounts: {
        ...s.unreadCounts,
        [conversationId]: 0,
      },
    })),

  incrementCrossAccountUnread: (accountId) =>
    set((s) => ({
      crossAccountUnread: {
        ...s.crossAccountUnread,
        [accountId]: (s.crossAccountUnread[accountId] || 0) + 1,
      },
    })),

  clearCrossAccountUnread: (accountId) =>
    set((s) => ({
      crossAccountUnread: {
        ...s.crossAccountUnread,
        [accountId]: 0,
      },
    })),

  updateLastMessage: (conversationId, timestamp, text, senderId, senderName) =>
    set((s) => ({
      conversations: s.conversations
        .map((c) => (c.id === conversationId ? {
          ...c,
          last_message_at: timestamp,
          ...(text !== undefined ? { last_message_text: text ?? null } : {}),
          ...(senderId !== undefined ? { last_message_sender_id: senderId ?? null } : {}),
          ...(senderName !== undefined ? { last_message_sender_name: senderName ?? null } : {}),
        } : c))
        .sort((a, b) => {
          const ta = a.last_message_at || a.created_at;
          const tb = b.last_message_at || b.created_at;
          return tb.localeCompare(ta);
        }),
    })),
}));

// Expose stores for debugging
if (typeof window !== "undefined") {
  (window as any).__convStore = useConversationStore;
}
